import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../ui/components/Button";
import { Select } from "../../../ui/components/Select";
import { TextField } from "../../../ui/components/TextField";
import { TextArea } from "../../../ui/components/TextArea";
import {
  CustomerContextData,
  generateCustomerContext,
} from "../../../services/contextApi";
import { useToast } from "./Toast/ToastContext";

interface CustomerContextSectionProps {
  customerName: string;
  existingContext?: CustomerContextData;
  onContextChange: (context: CustomerContextData) => void;
}

const CustomerContextSection: React.FC<CustomerContextSectionProps> = ({
  customerName,
  existingContext,
  onContextChange,
}) => {
  const { showToast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [contextData, setContextData] = useState<CustomerContextData>({
    customer_name: customerName,
    default_revenue_category: "",
    repeat_business_expected: "N",
    raw_input: "",
    final_context: "",
  });

  useEffect(() => {
    if (existingContext) {
      setContextData((prev) => ({ ...prev, ...existingContext }));
    }
  }, [existingContext]);

  useEffect(() => {
    setContextData((prev) => ({ ...prev, customer_name: customerName }));
  }, [customerName]);

  const handleFieldChange = useCallback(
    (field: keyof CustomerContextData, value: string | undefined) => {
      setContextData((prev) => {
        const updated = { ...prev, [field]: value };
        onContextChange(updated);
        return updated;
      });
    },
    [onContextChange]
  );

  const handleGenerate = async () => {
    const name = contextData.customer_name || customerName;
    if (!name?.trim()) {
      showToast("Customer name is required", "error");
      return;
    }
    if (!contextData.raw_input?.trim()) {
      showToast("Please describe what this customer buys", "error");
      return;
    }
    setIsGenerating(true);
    try {
      const response = await generateCustomerContext({
        customer_name: name,
        default_revenue_category: contextData.default_revenue_category,
        repeat_business_expected: contextData.repeat_business_expected,
        raw_input: contextData.raw_input,
      });
      if (response.success && response.data?.ai_rewritten_context) {
        const aiText = response.data.ai_rewritten_context;
        setContextData((prev) => {
          const updated = {
            ...prev,
            ai_rewritten_context: aiText,
            final_context: aiText,
          };
          onContextChange(updated);
          return updated;
        });
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to generate context", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const charCount = contextData.final_context?.length || 0;

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <span className="text-heading-3 font-heading-3 text-default-font">
        GL Classification Context
      </span>
      <p className="text-body font-body text-subtext-color">
        Provide context about this customer to improve automatic GL
        classification accuracy.
      </p>

      <div className="w-full items-start gap-4 grid grid-cols-2">
        <TextField className="w-full" label="Default Revenue Category">
          <TextField.Input
            placeholder="e.g. Book Sales, SaaS Subscription, Consulting"
            value={contextData.default_revenue_category || ""}
            onChange={(e) =>
              handleFieldChange("default_revenue_category", e.target.value)
            }
          />
        </TextField>
        <Select
          label="Repeat Business Expected"
          value={contextData.repeat_business_expected || "N"}
          onValueChange={(value) =>
            handleFieldChange("repeat_business_expected", value)
          }
        >
          <Select.Item value="Y">Yes</Select.Item>
          <Select.Item value="N">No</Select.Item>
        </Select>
      </div>

      <TextArea className="w-full" label="What does this customer buy from your company?">
        <TextArea.Input
          placeholder="e.g. Purchases bulk printed books quarterly for their retail chain, typically orders 5000-10000 units..."
          value={contextData.raw_input || ""}
          onChange={(e) => handleFieldChange("raw_input", e.target.value)}
        />
      </TextArea>

      <Button
        variant="neutral-secondary"
        onClick={handleGenerate}
        disabled={isGenerating}
        loading={isGenerating}
      >
        {isGenerating ? "Generating..." : "Generate AI Context"}
      </Button>

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-center justify-between">
          <span className="text-label font-label text-default-font">
            Final Context
          </span>
          <span
            className={`text-caption font-caption ${
              charCount > 750 ? "text-error-600" : "text-subtext-color"
            }`}
          >
            {charCount}/750
          </span>
        </div>
        <TextArea className="w-full">
          <TextArea.Input
            value={contextData.final_context || ""}
            onChange={(e) => {
              const value = e.target.value.slice(0, 750);
              handleFieldChange("final_context", value);
            }}
            placeholder="Generate using the button above, or type your own context here..."
          />
        </TextArea>
      </div>
    </div>
  );
};

export default CustomerContextSection;
